import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [userRole, setUserRole] = useState(""); // distributor, installer, enduser
  const [selectedDistributor, setSelectedDistributor] = useState("");
  const [distributors, setDistributors] = useState([]);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteToken, setInviteToken] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [inviteData, setInviteData] = useState(null); // Store invite details

  // Load distributors and check for invite token on mount
  useEffect(() => {
    
    // Check for invite token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('invite');
    if (token) {
      setInviteToken(token);
      setIsLogin(false); // Switch to registration form
      
      // Fetch invite details to get pre-filled distributor
      axios.get(`${API}/invite/${token}`)
        .then(response => {
          setInviteData(response.data);
          // Pre-fill email from invite
          if (response.data.email) {
            setEmail(response.data.email);
          }
          // Pre-fill distributor from inviter's distributor
          if (response.data.inviter_distributor) {
            setSelectedDistributor(response.data.inviter_distributor);
          }
          // Auto-set role based on invite type
          if (response.data.invite_type === 'installer') {
            setUserRole('installer');
          } else if (response.data.invite_type === 'securest') {
            setUserRole('securest');
          }
        })
        .catch(err => {
          console.error('Error fetching invite details:', err);
        });
    }
    
    // Load remembered email if exists
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail && !token) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
    
    // Fetch distributors list
    axios.get(`${API}/distributors`)
      .then(response => {
        setDistributors(response.data.distributors);
      })
      .catch(err => {
        console.error('Error fetching distributors:', err);
      });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Handle Remember Me - save or remove email
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      onLogin(response.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!firstName || !lastName) {
      setError("Please provide your first and last name");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (!userRole) {
      setError("Please select your role");
      return;
    }

    if ((userRole === "distributor" || userRole === "installer") && !selectedDistributor) {
      setError("Please select your distributor");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        company_name: companyName,
        distributor: (userRole === "distributor" || userRole === "installer") ? selectedDistributor : null,
        preferred_distributor: userRole === "installer" ? selectedDistributor : null,
        invite_token: inviteToken || null
      });
      
      const approvalMessage = userRole === "distributor" 
        ? "Registration successful! Your request has been sent to your distributor admin and ZKTeco UK for approval."
        : "Registration successful! Your request has been sent to ZKTeco UK for approval.";
      
      setSuccess(approvalMessage);
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setFirstName("");
      setLastName("");
      setCompanyName("");
      setUserRole("");
      setSelectedDistributor("");
      
      // Switch to login after 5 seconds
      setTimeout(() => {
        setIsLogin(true);
        setSuccess("");
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src="/api/logos/ZKTeco_zkteco.png" 
              alt="ZKTeco UK" 
              className="h-20 object-contain"
              onError={(e) => {
                // Fallback to icon if logo fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl items-center justify-center shadow-lg hidden">
              <Calculator className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-slate-900">ZKTeco UK</h1>
          <p className="text-slate-600 mt-2">Sales Configurator</p>
        </div>

        {/* Login/Register Card */}
        <Card>
          <CardHeader>
            <CardTitle>{isLogin ? "Login" : "Create Account"}</CardTitle>
            <CardDescription>
              {isLogin 
                ? "Enter your credentials to access the configurator" 
                : "Register for access to the speedlane configurator"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={isLogin ? handleLogin : handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@company.com"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="password-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        minLength={6}
                        data-testid="confirm-password-input"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="text-sm text-red-600">Passwords do not match</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                      <Input
                        id="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="John"
                        required
                        data-testid="firstname-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
                      <Input
                        id="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Smith"
                        required
                        data-testid="lastname-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Company Name (Optional)</Label>
                    <Input
                      id="company"
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Your Company Ltd"
                      data-testid="company-input"
                    />
                  </div>

                  {userRole === "securest" ? (
                    <div data-testid="securest-invite-info" className="p-3 bg-teal-50 border border-teal-200 rounded text-xs text-teal-800">
                      You've been invited to <strong>Securest</strong>. Your account will give you access to the Securest configurator once ZKTeco UK approves it.
                    </div>
                  ) : (
                  <div className="space-y-2">
                    <Label htmlFor="role">I am a <span className="text-red-500">*</span></Label>
                    <Select value={userRole} onValueChange={setUserRole} required>
                      <SelectTrigger data-testid="role-select">
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="distributor">Distributor</SelectItem>
                        <SelectItem value="installer">Installer</SelectItem>
                        <SelectItem value="enduser">End User</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  )}

                  {userRole === "distributor" && (
                    <div className="space-y-2">
                      <Label htmlFor="distributor">Select Your Distributor <span className="text-red-500">*</span></Label>
                      <Select value={selectedDistributor} onValueChange={setSelectedDistributor} required>
                        <SelectTrigger data-testid="distributor-select">
                          <SelectValue placeholder="Choose distributor" />
                        </SelectTrigger>
                        <SelectContent>
                          {distributors.map((dist) => (
                            <SelectItem key={dist} value={dist}>
                              {dist}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {inviteData?.inviter_distributor && selectedDistributor === inviteData.inviter_distributor ? (
                        <p className="text-xs text-green-600">
                          ✓ Auto-assigned from {inviteData.inviter_name || 'your inviter'}'s organisation. You can change this if needed.
                        </p>
                      ) : (
                        <p className="text-xs text-slate-500">
                          Your request will be sent to both your distributor admin and ZKTeco UK
                        </p>
                      )}
                    </div>
                  )}

                  {userRole === "installer" && (
                    <div className="space-y-2">
                      <Label htmlFor="distributor">Preferred Distributor <span className="text-red-500">*</span></Label>
                      <Select value={selectedDistributor} onValueChange={setSelectedDistributor} required>
                        <SelectTrigger data-testid="distributor-select">
                          <SelectValue placeholder="Select your preferred distributor" />
                        </SelectTrigger>
                        <SelectContent>
                          {distributors.map((dist) => (
                            <SelectItem key={dist} value={dist}>
                              {dist}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {inviteData?.inviter_distributor && selectedDistributor === inviteData.inviter_distributor ? (
                        <p className="text-xs text-green-600">
                          ✓ Auto-assigned from {inviteData.inviter_name || 'your inviter'}'s distributor. You can change this if needed.
                        </p>
                      ) : (
                        <p className="text-xs text-slate-500">
                          Select the distributor you typically work with
                        </p>
                      )}
                    </div>
                  )}

                  {userRole === "enduser" && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                      Your registration will be reviewed by ZKTeco UK (adam.jagger@zkteco.co.uk)
                    </div>
                  )}
                </>
              )}

              {isLogin && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="rememberMe" 
                      checked={rememberMe}
                      onCheckedChange={setRememberMe}
                    />
                    <Label 
                      htmlFor="rememberMe" 
                      className="text-sm font-normal cursor-pointer"
                    >
                      Remember my email
                    </Label>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              {error && (
                <Alert variant="destructive" data-testid="error-alert">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="bg-green-50 border-green-200" data-testid="success-alert">
                  <AlertDescription className="text-green-800">{success}</AlertDescription>
                </Alert>
              )}

              <Button 
                type="submit" 
                className="w-full"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? "Please wait..." : (isLogin ? "Login" : "Register")}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError("");
                  setSuccess("");
                }}
                className="text-sm text-slate-600 hover:text-slate-900 underline"
                data-testid="toggle-auth-button"
              >
                {isLogin 
                  ? "Don't have an account? Register" 
                  : "Already have an account? Login"}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> New registrations require admin approval. 
            You'll receive access once your account is approved.
          </p>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Reset Password</CardTitle>
                <CardDescription>
                  Enter your email address and we'll send you instructions to reset your password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {success && (
                    <Alert className="bg-green-50 border-green-200">
                      <AlertDescription className="text-green-800">{success}</AlertDescription>
                    </Alert>
                  )}
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email Address</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        if (!resetEmail) {
                          setError("Please enter your email address");
                          return;
                        }
                        setError("");
                        setLoading(true);
                        try {
                          await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
                          setSuccess("Password reset instructions sent to your email!");
                          setTimeout(() => {
                            setShowForgotPassword(false);
                            setSuccess("");
                            setResetEmail("");
                          }, 3000);
                        } catch (err) {
                          setError(err.response?.data?.detail || "Failed to send reset email");
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="flex-1"
                    >
                      {loading ? "Sending..." : "Send Reset Link"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmail("");
                        setError("");
                        setSuccess("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;